import { Component, inject, signal, PLATFORM_ID, AfterViewInit } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { GoogleAnalyticsService } from '../../services/google-analytics/google-analytics.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-cookie-banner',
  templateUrl: './cookie-banner.html',
  styleUrl: './cookie-banner.css',
  standalone: true
})
export class CookieBanner implements AfterViewInit {

  // DEPENDENCIES

  private readonly platform_id = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly ga_service = inject(GoogleAnalyticsService);

  // STATE

  protected readonly show_banner = signal(false);
  protected readonly show_preferences = signal(false);
  protected readonly analytics_consent = signal(false);
  protected readonly marketing_consent = signal(false);

  // CONSTANTS

  private readonly COOKIE_NAME = 'cookie_consent';
  private readonly MAX_AGE_YEAR = 31536000; // 1 year in seconds

  // LIFECYCLE

  public ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platform_id)) {
      this.initConsent();
    }
  }

  // EVENT HANDLERS

  protected onAccept(): void {
    this.saveConsent(true, true);
  }

  protected onDeny(): void {
    this.saveConsent(false, false);
  }

  protected onSelectPreferences(): void {
    this.show_preferences.set(true);
  }

  protected onHidePreferences(): void {
    this.show_preferences.set(false);
  }

  protected onToggleAnalytics(): void {
    this.analytics_consent.update(v => !v);
  }

  protected onToggleMarketing(): void {
    this.marketing_consent.update(v => !v);
  }

  protected onSavePreferences(): void {
    this.saveConsent(this.analytics_consent(), this.marketing_consent());
  }

  // PRIVATE HELPERS

  private initConsent(): void {

    if (!environment.production) return;

    const cookie_value = this.getCookie(this.COOKIE_NAME);

    if (!cookie_value) {
      this.show_banner.set(true);
      return;
    }

    try {
      const consent = JSON.parse(decodeURIComponent(cookie_value));
      if (consent && typeof consent.analytics === 'boolean' && typeof consent.marketing === 'boolean') {
        this.ga_service.updateConsent(consent.analytics, consent.marketing);
        return;
      }
    } catch {
      this.show_banner.set(true);
    }

  }

  private saveConsent(analytics: boolean, marketing: boolean): void {

    const consent = { analytics, marketing };
    const secure = this.document.location.protocol === 'https:' ? '; Secure' : '';
    const value = encodeURIComponent(JSON.stringify(consent));
    const cookie_str = `${this.COOKIE_NAME}=${value}; Max-Age=${this.MAX_AGE_YEAR}; Path=/; SameSite=Lax${secure}`;
    
    this.document.cookie = cookie_str;
    this.ga_service.updateConsent(analytics, marketing);
    this.show_banner.set(false);

  }

  private getCookie(name: string): string | null {

    const name_eq = name + '=';
    const ca = this.document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(name_eq) === 0) return c.substring(name_eq.length, c.length);
    }
    
    return null;
    
  }

}
